import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * A field of Settings that another screen can point at. The Overview's rows
 * all open Build & deploy, and without a pointer every one of them landed on
 * the top of the same page, leaving the reader to find the field they had
 * just clicked on.
 */
export type SettingsFocus =
  | 'source'
  | 'build'
  | 'root-directory'
  | 'port'
  | 'start-command'
  | 'health-check';

/**
 * Where to look when the field itself is not on the page. The start command
 * only exists on the Buildpack route; on a Dockerfile app the Overview row
 * still shows it, and the answer is the build method that hides it.
 */
const FALLBACK: Partial<Record<SettingsFocus, SettingsFocus>> = {
  'start-command': 'build',
};

// Both beats of the pulse (index.css).
const SPOTLIGHT_MS = 2400;
// The tab's form can render a beat after the app does.
const FIND_FOR_MS = 1500;
// The fields take the skeleton's place, and the pulse comes after that, not
// in the same frame: lit up as it appeared, the field read as a glitch.
const SETTLE_MS = 350;

const find = (focus: SettingsFocus) =>
  document.querySelector<HTMLElement>(`[data-settings-focus="${focus}"]`)
  || (FALLBACK[focus]
    ? document.querySelector<HTMLElement>(`[data-settings-focus="${FALLBACK[focus]}"]`)
    : null);

/**
 * On arrival with a field to point at, once it is on the page: scroll it to
 * the middle of the screen, pulse it softly twice, and put the cursor in it
 * when it is something to type in. Then the pointer is dropped from the
 * history entry, so going back and forth does not point again.
 */
export const useSettingsSpotlight = (isReady: boolean) => {
  const location = useLocation();
  const focus = (location.state as { settingsFocus?: SettingsFocus } | null)?.settingsFocus;

  useEffect(() => {
    if (!focus || !isReady) {
      return;
    }
    const startedAt = Date.now();
    let raf = 0;
    let settle = 0;

    const spotlight = (target: HTMLElement) => {
      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      target.classList.add('b4-spotlight');
      const input = target.querySelector<HTMLInputElement>('input[type="text"], input:not([type]), textarea');
      if (input && !input.disabled) {
        input.focus({ preventScroll: true });
      }
      // Not undone by the cleanup below: dropping the pointer re-runs this
      // effect, and the pulse has to outlive that.
      window.setTimeout(() => target.classList.remove('b4-spotlight'), SPOTLIGHT_MS);
      // Straight on the history entry: the router reads its state from there
      // on back and forward, and a replace through it re-rendered the whole
      // screen for a value nothing else reads.
      const { usr, ...entry } = (window.history.state || {}) as { usr?: unknown };
      if (usr) {
        window.history.replaceState({ ...entry, usr: null }, '');
      }
    };

    const look = () => {
      const target = find(focus);
      if (!target) {
        if (Date.now() - startedAt < FIND_FOR_MS) {
          raf = requestAnimationFrame(look);
        }
        return;
      }
      settle = window.setTimeout(() => spotlight(target), SETTLE_MS);
    };
    raf = requestAnimationFrame(look);

    return () => {
      cancelAnimationFrame(raf);
      window.clearTimeout(settle);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focus, isReady]);
};
